import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EncounterAudioService } from './EncounterAudioService.mjs';

const GameAudioContext = createContext(null);
const DEFAULT_VOLUMES = Object.freeze({ ambience: 0.5, music: 0.55, effects: 0.75, ui: 0.65 });

function clamp(value, min = 0, max = 1) {
  return Math.max(min, Math.min(max, Number(value) || 0));
}

function fadeAudio(track, target, durationMs, masterVolume) {
  if (!track?.audio) return;
  if (track.fadeFrame) window.cancelAnimationFrame(track.fadeFrame);
  const audio = track.audio;
  const start = audio.volume;
  const end = clamp(target * masterVolume);
  const startedAt = performance.now();
  const duration = Math.max(0, Number(durationMs) || 0);

  const tick = (now) => {
    const progress = duration ? clamp((now - startedAt) / duration) : 1;
    audio.volume = start + (end - start) * progress;
    if (progress < 1) track.fadeFrame = window.requestAnimationFrame(tick);
    else track.fadeFrame = 0;
  };
  track.fadeFrame = window.requestAnimationFrame(tick);
}

export function GameAudioProvider({ children }) {
  const tracksRef = useRef(new Map());
  const desiredLoopsRef = useRef(new Map());
  const oneShotsRef = useRef(new Set());
  const cooldownsRef = useRef(new Map());
  const audioContextRef = useRef(null);
  const unlockedRef = useRef(false);
  const mutedRef = useRef(false);
  const volumesRef = useRef({ ...DEFAULT_VOLUMES });
  const encounterAudioRef = useRef(null);
  const [unlocked, setUnlocked] = useState(false);
  const [muted, setMutedState] = useState(false);
  const [volumes, setVolumesState] = useState({ ...DEFAULT_VOLUMES });

  const busVolume = useCallback((bus) => mutedRef.current ? 0 : clamp(volumesRef.current[bus] ?? 1), []);

  const playEncounterDiscovery = useCallback((encounter, reason) => {
    if (!encounterAudioRef.current) {
      let storage = null;
      try { storage = window.sessionStorage; } catch { /* Private browsing may deny storage. */ }
      encounterAudioRef.current = new EncounterAudioService({
        getContext: () => audioContextRef.current,
        isUnlocked: () => unlockedRef.current,
        getVolume: () => busVolume('effects'),
        fetchAudio: (url, options) => window.fetch(url, options),
        createAudio: () => new window.Audio(),
        storage,
        origin: window.location.origin,
      });
    }
    return encounterAudioRef.current.playDiscovery(encounter, reason);
  }, [busVolume]);
  const stopEncounterSound = useCallback((id) => encounterAudioRef.current?.stop(id), []);

  const startLoop = useCallback(async (id, descriptor) => {
    if (!id || !descriptor?.url || !unlockedRef.current) return false;
    let track = tracksRef.current.get(id);
    if (!track || track.url !== descriptor.url) {
      if (track?.audio) {
        track.audio.pause();
        if (track.fadeFrame) window.cancelAnimationFrame(track.fadeFrame);
      }
      const audio = new window.Audio(descriptor.url);
      audio.loop = true;
      audio.preload = 'auto';
      audio.volume = 0;
      track = { audio, url: descriptor.url, bus: descriptor.bus, baseVolume: descriptor.volume, fadeFrame: 0 };
      tracksRef.current.set(id, track);
    }
    track.bus = descriptor.bus;
    track.baseVolume = descriptor.volume;
    try {
      if (track.audio.paused) await track.audio.play();
      fadeAudio(track, descriptor.volume, descriptor.fadeMs, busVolume(descriptor.bus));
      return true;
    } catch {
      return false;
    }
  }, [busVolume]);

  const unlock = useCallback(async () => {
    if (unlockedRef.current && (!audioContextRef.current || audioContextRef.current.state === 'running')) return true;
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        audioContextRef.current = audioContextRef.current || new AudioContextClass();
        if (audioContextRef.current.state === 'suspended') await audioContextRef.current.resume();
      }
      unlockedRef.current = true;
      setUnlocked(true);
      desiredLoopsRef.current.forEach((descriptor, id) => { startLoop(id, descriptor); });
      return true;
    } catch {
      return false;
    }
  }, [startLoop]);

  useEffect(() => {
    const handleGesture = (event) => { if (event.isTrusted) void unlock(); };
    window.addEventListener('pointerdown', handleGesture, { passive: true });
    window.addEventListener('touchstart', handleGesture, { passive: true });
    window.addEventListener('click', handleGesture);
    window.addEventListener('keydown', handleGesture);
    return () => {
      window.removeEventListener('pointerdown', handleGesture);
      window.removeEventListener('touchstart', handleGesture);
      window.removeEventListener('click', handleGesture);
      window.removeEventListener('keydown', handleGesture);
    };
  }, [unlock]);

  const playLoop = useCallback((id, url, options = {}) => {
    const descriptor = {
      url,
      bus: options.bus || 'ambience',
      volume: clamp(options.volume ?? 0.5),
      fadeMs: Math.max(0, Number(options.fadeMs ?? 700)),
    };
    desiredLoopsRef.current.set(id, descriptor);
    return startLoop(id, descriptor);
  }, [startLoop]);

  const stopLoop = useCallback((id, fadeMs = 500) => {
    desiredLoopsRef.current.delete(id);
    const track = tracksRef.current.get(id);
    if (!track) return;
    fadeAudio(track, 0, fadeMs, 1);
    window.setTimeout(() => {
      const current = tracksRef.current.get(id);
      if (current !== track || desiredLoopsRef.current.has(id)) return;
      track.audio.pause();
      track.audio.currentTime = 0;
      tracksRef.current.delete(id);
    }, Math.max(0, fadeMs) + 40);
  }, []);

  const stopBus = useCallback((bus, fadeMs = 500) => {
    [...desiredLoopsRef.current.entries()].forEach(([id, descriptor]) => {
      if (descriptor.bus === bus) stopLoop(id, fadeMs);
    });
  }, [stopLoop]);

  const canPlay = useCallback((id, cooldownMs) => {
    const now = performance.now();
    const previous = cooldownsRef.current.get(id) || 0;
    if (now - previous < cooldownMs) return false;
    cooldownsRef.current.set(id, now);
    return true;
  }, []);

  const playOneShot = useCallback(async (id, url, options = {}) => {
    const bus = options.bus || 'effects';
    const cooldownMs = Math.max(0, Number(options.cooldownMs ?? 90));
    if (!url || !canPlay(id, cooldownMs)) return false;
    if (!unlockedRef.current && !(await unlock())) return false;
    const audio = new window.Audio(url);
    audio.preload = 'auto';
    audio.volume = clamp((options.volume ?? 0.75) * busVolume(bus));
    oneShotsRef.current.add(audio);
    const cleanup = () => oneShotsRef.current.delete(audio);
    audio.addEventListener('ended', cleanup, { once: true });
    audio.addEventListener('error', cleanup, { once: true });
    try {
      await audio.play();
      return true;
    } catch {
      cleanup();
      return false;
    }
  }, [busVolume, canPlay, unlock]);

  const playTone = useCallback(async (id, options = {}) => {
    const cooldownMs = Math.max(0, Number(options.cooldownMs ?? 120));
    if (!canPlay(id, cooldownMs)) return false;
    if (!unlockedRef.current && !(await unlock())) return false;
    const context = audioContextRef.current;
    if (!context) return false;
    const bus = options.bus || 'effects';
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const now = context.currentTime;
    const duration = clamp(options.duration ?? 0.14, 0.04, 1.2);
    oscillator.type = options.type || 'sine';
    oscillator.frequency.setValueAtTime(Math.max(40, Number(options.frequency ?? 280)), now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(40, Number(options.endFrequency ?? options.frequency ?? 280)), now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, clamp((options.volume ?? 0.22) * busVolume(bus))), now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
    return true;
  }, [busVolume, canPlay, unlock]);

  const setMuted = useCallback((nextMuted) => {
    const value = Boolean(nextMuted);
    mutedRef.current = value;
    setMutedState(value);
    tracksRef.current.forEach((track) => fadeAudio(track, track.baseVolume, 180, busVolume(track.bus)));
    encounterAudioRef.current?.syncVolume();
  }, [busVolume]);

  const setBusVolume = useCallback((bus, value) => {
    if (!(bus in DEFAULT_VOLUMES)) return;
    const nextValue = clamp(value);
    volumesRef.current = { ...volumesRef.current, [bus]: nextValue };
    encounterAudioRef.current?.syncVolume();
    setVolumesState(volumesRef.current);
    tracksRef.current.forEach((track) => {
      if (track.bus === bus) fadeAudio(track, track.baseVolume, 180, busVolume(bus));
    });
  }, [busVolume]);

  useEffect(() => () => {
    encounterAudioRef.current?.stop();
    desiredLoopsRef.current.clear();
    tracksRef.current.forEach((track) => {
      if (track.fadeFrame) window.cancelAnimationFrame(track.fadeFrame);
      track.audio.pause();
    });
    oneShotsRef.current.forEach((audio) => audio.pause());
    tracksRef.current.clear();
    oneShotsRef.current.clear();
    audioContextRef.current?.close?.()?.catch?.(() => {});
  }, []);

  const value = useMemo(() => ({
    muted,
    unlocked,
    volumes,
    unlock,
    setMuted,
    setBusVolume,
    playLoop,
    stopLoop,
    stopBus,
    playOneShot,
    playTone,
    playEncounterDiscovery,
    stopEncounterSound,
  }), [muted, playLoop, playOneShot, playTone, playEncounterDiscovery, stopEncounterSound, setBusVolume, setMuted, stopBus, stopLoop, unlock, unlocked, volumes]);

  return <GameAudioContext.Provider value={value}>{children}</GameAudioContext.Provider>;
}

export function useGameAudio() {
  const context = useContext(GameAudioContext);
  if (!context) throw new Error('useGameAudio muss innerhalb des GameAudioProvider verwendet werden.');
  return context;
}

export function useOptionalGameAudio() {
  return useContext(GameAudioContext);
}
